import './FooterNav.scss';
import { useCallback } from 'react';

import { CLICK_IDS } from '@sunflower-games/analytics-events';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { ONBOARDING_CONFIG } from '../../features/onboarding/constants';
import { MenuOnboarding } from '../../features/onboarding/MenuOnboarding';
import { useOnboarding } from '../../features/onboarding/useOnboarding';
import { analytics } from '../../services/analytics';
import { useFlag } from '../../services/launchDarkly';
import { type FullPageDialogs } from '../../stores/ui/types';
import { useStores } from '../../stores/useStores';
import { CoinImage } from '../CoinImage/CoinImage';
import { getSlideOrder } from '../FullPageDialogs/config';
import { NotificationDotController } from '../Notifications/NotificationDotController';
import CrownIcon from '../SVGIcons/CrownIcon';
import { CrownRoundedIcon } from '../SVGIcons/CrownRoundedIcon';
import DailyRewardsIcon from '../SVGIcons/DailyRewardsIcon';
import { PrimeIcon } from '../SVGIcons/PrimeIcon';

import { AccountIcon } from './AccountIcon';
import EuroStoreIcon from './EuroStoreIcon';
import NavItem from './NavItem';
import RedeemIcon from './RedeemIcon';
import StoreIcon from './StoreIcon';

const getCurrencyStoreIcon = (currencyCode?: string) => {
  switch (currencyCode) {
    case 'EUR':
      return <EuroStoreIcon />;
    case 'USD':
    case 'CAD':
    default:
      return <StoreIcon />;
  }
};

const OldDesignFooterNav = observer(() => {
  const { uiStore, bingoStore, loyaltyProgramStore, appStore, pocStore, userStore } = useStores();
  const { t } = useTranslation();
  const redeemModeAvailable = useFlag('redeem_mode_available', true);

  const { showOnboarding, tooltipStyles, setReferenceElement, setPopperElement } = useOnboarding(
    ONBOARDING_CONFIG.MENU_ACTIONS,
    'bottom',
    [-5, 30],
  );

  const preventPurchase = useFlag('prevent_purchase', false);
  const loyaltyProgramFlag = useFlag('loyalty_program', true);

  const handleOpenFullPageModal = useCallback(
    (dialog: FullPageDialogs | null) => {
      if (!showOnboarding) {
        if (dialog === 'store' && preventPurchase) {
          uiStore.setOpenFullPageDialog('prevent_purchase');
          return;
        }
        uiStore.setOpenFullPageDialog(dialog);
      }
    },
    [preventPurchase, showOnboarding, uiStore],
  );

  const handleRewardOpen = () => {
    handleOpenFullPageModal('rewards');
    void analytics.sendEvent('rewards_menu', {
      event_action: 'click',
      button_location: 'footer',
    });
  };

  const { currentTier } = loyaltyProgramStore.userLoyalty ?? {};

  const handleOpenStore = () => {
    if (showOnboarding) {
      return;
    }

    void analytics.sendEvent('purchase', {
      step_name: 'store',
      event_action: 'click',
      triggered_by: 'click',
      store_type: 'store',
      store_segment: loyaltyProgramStore.currentTierKey,
      button_location: 'menu',
      click_id: CLICK_IDS.PURCHASE.FOOTER_OPEN_STORE,
      store_key: appStore.currentStore?.key || '',
      sign_up_date: userStore.profile?.createdAt || '',
    });

    handleOpenFullPageModal('store');
  };

  const handleOpenRedeem = () => {
    if (showOnboarding || uiStore.openFullPageDialog === 'redeem') {
      return;
    }
    void analytics.sendEvent('redeem_menu', {
      event_action: 'click',
      triggered_by: 'click',
      button_location: 'footer',
    });
    handleOpenFullPageModal('redeem');
  };

  const handleOpenBingo = () => {
    if (showOnboarding) {
      return;
    }
    void analytics.sendEvent('bingo_button_clicked', {
      triggered_by: 'footer_nav',
      event_action: 'click',
      event_category: 'metagame_bingo',
    });
    handleOpenFullPageModal('bingo');
  };

  const disableControls =
    (uiStore.openFullPageDialog === 'loyalty_program' && loyaltyProgramStore.isLoyaltyProgramDialogOpenWithTutorial) ||
    (uiStore.openFullPageDialog &&
      ['new_tier_congrats', 'tier_monthly_bonus', 'tier_birthday_gift'].includes(uiStore.openFullPageDialog));

  return (
    <>
      <div
        ref={setReferenceElement}
        className={classNames('footer-nav', {
          'footer-nav_onboarding': showOnboarding,
          'controls-disabled': disableControls,
        })}
      >
        <div
          ref={(el) => {
            if (el) {
              el.style.setProperty('--middle-point', el.clientWidth / 2 - 14 + 'px');
            }
          }}
          className={classNames(
            'footer-nav__shifter',
            'footer-nav__shifter_' + getSlideOrder(uiStore.openFullPageDialog),
            {
              'onboarding-arrow': showOnboarding,
            },
          )}
        >
          <div className="footer-nav__shifter-arrow" />
        </div>
        <NavItem dialog="store" title={t('footer.store')} onClick={handleOpenStore}>
          {getCurrencyStoreIcon(userStore.profile?.currency?.code)}
        </NavItem>
        <NavItem
          dialog="redeem"
          disabled={!redeemModeAvailable}
          title={redeemModeAvailable ? t('common.redeemLowerCase') : undefined}
          onClick={handleOpenRedeem}
        >
          {redeemModeAvailable ? (
            <div className={pocStore.shouldShowHangingSessionMarker ? 'hanging-poc-marker' : undefined}>
              <RedeemIcon />
            </div>
          ) : (
            <div className="redeem-disabled">
              <p className="redeem-disabled-text">{t('footer.comingSoon')}</p>
            </div>
          )}
        </NavItem>
        <NavItem dialog={null} title={t('footer.home')} onClick={handleOpenFullPageModal}>
          <div className="nav-item__icon-home">
            <CrownRoundedIcon />
            <CoinImage className="nav-item__icon-home__coin" />
          </div>
        </NavItem>
        <NavItem dialog="rewards" title={t('rewards.title')} onClick={handleRewardOpen}>
          <DailyRewardsIcon />
          <div className="dot_wrapper">
            <NotificationDotController type="small" />
          </div>
        </NavItem>
        {/* TODO: FIX THIS WHEN REBRANDING IS DONE */}
        {!bingoStore.bingoEnabled && (
          <NavItem
            additionalDialogs={['loyalty_program']}
            dialog="account"
            title={t('footer.account')}
            onClick={handleOpenFullPageModal}
          >
            {loyaltyProgramFlag && currentTier ? (
              <div className="crown-wrapper" style={{ backgroundColor: currentTier?.color }}>
                <CrownIcon />
              </div>
            ) : (
              <AccountIcon />
            )}
          </NavItem>
        )}
        {bingoStore.bingoEnabled && (
          <NavItem dialog="bingo" title="Bingo" onClick={handleOpenBingo}>
            <PrimeIcon />
          </NavItem>
        )}
      </div>

      {showOnboarding && (
        <MenuOnboarding
          overlayClassName="footer-nav__onboarding-overlay"
          setPopperElement={setPopperElement}
          tooltipStyles={tooltipStyles}
        />
      )}
    </>
  );
});

export default OldDesignFooterNav;
